import React, { useState, useEffect, useCallback } from 'react';
import ordersApiService from '../services/ordersApi';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  const statusColors = {
    new: 'bg-blue-100 text-blue-800 border-blue-200',
    confirmed: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    preparing: 'bg-purple-100 text-purple-800 border-purple-200',
    ready: 'bg-green-100 text-green-800 border-green-200',
    completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
  };

  const statusTexts = {
    new: 'Новый',
    confirmed: 'Подтвержден',
    preparing: 'Готовится',
    ready: 'Готов',
    completed: 'Завершен',
    cancelled: 'Отменен',
  };

  // Функция для получения заказов с API
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: currentPage,
        page_size: 20,
        status: selectedStatus,
        search: searchTerm.trim() || undefined,
      };

      const data = await ordersApiService.getAdminOrdersList(params);
      
      setOrders(data.orders || []);
      setStats(data.stats || {});
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);
      
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
      if (error.message.includes('403')) {
        setError('У вас нет прав для просмотра заказов');
      } else if (error.message.includes('401')) {
        setError('Необходима авторизация');
      } else {
        setError('Ошибка загрузки заказов: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, searchTerm, currentPage]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Функция для обновления статуса заказа
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdating(true);
      
      await ordersApiService.updateOrderStatus(orderId, newStatus);

      // Обновляем локальные данные
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Перезагружаем данные для обновления статистики
      loadOrders();
      
    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
      setError('Ошибка обновления статуса: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount) => {
    // Округляем до целого числа и форматируем без копеек
    const rounded = Math.round(Number(amount) || 0);
    return `${rounded.toLocaleString('ru-RU')} ₽`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatOrderNumber = (orderNumber) => {
    // Если номер заказа уже в новом формате (содержит буквы), возвращаем как есть
    if (orderNumber && /[A-Z]/.test(orderNumber)) {
      return `#${orderNumber}`;
    }
    // Иначе выводим как было
    return `#${orderNumber}`;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Сбрасываем на первую страницу при поиске
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1); // Сбрасываем на первую страницу при фильтрации
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowModal(false);
    // Очищаем состояния загрузки изображений при закрытии модального окна
    setImageLoadingStates({});
  };

  const handleImageLoad = (imageKey) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageKey]: 'loaded'
    }));
  };

  const handleImageError = (imageKey) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageKey]: 'error'
    }));
  };

  const handleImageStart = (imageKey) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [imageKey]: 'loading'
    }));
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление заказами</h1>
          <p className="text-gray-600 mt-1">Отслеживайте и управляйте всеми заказами</p>
        </div>
        <div className="text-sm text-gray-500">
          Всего заказов: {totalCount}
        </div>
      </div>

      {/* Ошибки */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Статистика */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-primary-600">{totalCount}</div>
          <div className="text-sm text-gray-600">Всего</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-blue-600">{stats.new || 0}</div>
          <div className="text-sm text-gray-600">Новые</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-yellow-600">{stats.confirmed || 0}</div>
          <div className="text-sm text-gray-600">Подтверждены</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-purple-600">{stats.preparing || 0}</div>
          <div className="text-sm text-gray-600">Готовятся</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-green-600">{stats.ready || 0}</div>
          <div className="text-sm text-gray-600">Готовы</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-emerald-600">{stats.completed || 0}</div>
          <div className="text-sm text-gray-600">Завершены</div>
        </div>
        <div className="card text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-red-600">{stats.cancelled || 0}</div>
          <div className="text-sm text-gray-600">Отменены</div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Поиск по номеру заказа (#AB12) или клиенту..."
              className="input-field"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex gap-2">
            <select
              className="input-field min-w-[150px]"
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              <option value="all">Все статусы</option>
              <option value="new">Новые</option>
              <option value="confirmed">Подтверждены</option>
              <option value="preparing">Готовятся</option>
              <option value="ready">Готовы</option>
              <option value="completed">Завершены</option>
              <option value="cancelled">Отменены</option>
            </select>
            <button 
              className="btn-outline px-3"
              onClick={loadOrders}
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Таблица заказов */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead>
              <tr className="table-header">
                <th className="table-cell text-left">№ Заказа</th>
                <th className="table-cell text-left">Клиент</th>
                <th className="table-cell text-left">Телефон</th>
                <th className="table-cell text-left">Сумма</th>
                <th className="table-cell text-left">Оплата</th>
                <th className="table-cell text-left">Статус</th>
                <th className="table-cell text-left">Дата</th>
                <th className="table-cell text-left">Действия</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="table-cell text-center py-8 text-gray-500">
                    {loading ? 'Загрузка...' : 'Заказы не найдены'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="table-row hover:bg-gray-50">
                    <td className="table-cell font-medium">{formatOrderNumber(order.order_number)}</td>
                    <td className="table-cell">
                      <div>
                        <div className="font-medium">{order.customer_name}</div>
                        {order.customer_email && (
                          <div className="text-sm text-gray-500">{order.customer_email}</div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">{order.customer_phone || 'Не указан'}</td>
                    <td className="table-cell font-medium">{formatCurrency(order.total_price)}</td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        {['card', 'card_online', 'card_courier'].includes(order.payment_method) ? (
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                            <span className="text-sm text-blue-700 font-medium">💳 Картой</span>
                          </div>
                        ) : order.payment_method === 'cash' ? (
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                            <span className="text-sm text-green-700 font-medium">💵 Наличными</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            {order.payment_method_display || order.payment_method || 'Не указан'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`status-badge ${statusColors[order.status]}`}>
                        {statusTexts[order.status]}
                      </span>
                    </td>
                    <td className="table-cell text-sm">{formatDate(order.created_at)}</td>
                    <td className="table-cell">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openOrderModal(order)}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          Подробнее
                        </button>
                        {order.status !== 'completed' && order.status !== 'cancelled' && (
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            disabled={updating}
                            className="text-xs border border-gray-300 rounded px-2 py-1"
                          >
                            <option value="new">Новый</option>
                            <option value="confirmed">Подтвержден</option>
                            <option value="preparing">Готовится</option>
                            <option value="ready">Готов</option>
                            <option value="completed">Завершен</option>
                            <option value="cancelled">Отменен</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn-outline px-3 py-2 disabled:opacity-50"
          >
            Назад
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded ${
                    currentPage === pageNum
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn-outline px-3 py-2 disabled:opacity-50"
          >
            Вперед
          </button>
        </div>
      )}

      {/* Модальное окно с деталями заказа */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Заголовок с градиентом */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold">Заказ {formatOrderNumber(selectedOrder.order_number)}</h3>
                  <p className="text-primary-100 mt-1">Детальная информация о заказе</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    selectedOrder.status === 'new' ? 'bg-blue-500 text-white' :
                    selectedOrder.status === 'confirmed' ? 'bg-yellow-500 text-white' :
                    selectedOrder.status === 'preparing' ? 'bg-purple-500 text-white' :
                    selectedOrder.status === 'ready' ? 'bg-green-500 text-white' :
                    selectedOrder.status === 'completed' ? 'bg-emerald-500 text-white' :
                    'bg-red-500 text-white'
                  }`}>
                    {statusTexts[selectedOrder.status]}
                  </span>
                  <button 
                    onClick={closeOrderModal} 
                    className="text-white hover:text-primary-200 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-20"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Основная информация */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Информация о клиенте */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">Клиент</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-600 w-16">Имя:</span>
                      <span className="text-gray-900">{selectedOrder.customer_name}</span>
                    </div>
                    {selectedOrder.customer_phone && (
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-16">Телефон:</span>
                        <a href={`tel:${selectedOrder.customer_phone}`} className="text-primary-600 hover:text-primary-800">
                          {selectedOrder.customer_phone}
                        </a>
                      </div>
                    )}
                    {selectedOrder.customer_email && (
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-600 w-16">Email:</span>
                        <a href={`mailto:${selectedOrder.customer_email}`} className="text-primary-600 hover:text-primary-800">
                          {selectedOrder.customer_email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Детали заказа */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">Детали</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-600 w-16">Дата:</span>
                      <span className="text-gray-900">{formatDate(selectedOrder.created_at)}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-600 w-16">Тип:</span>
                      <span className="text-gray-900">
                        {selectedOrder.order_type === 'delivery' ? '🚚 Доставка' : '🏪 В заведении'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-600 w-16">Оплата:</span>
                      <div className="flex items-center">
                        {['card', 'card_online', 'card_courier'].includes(selectedOrder.payment_method) ? (
                          <>
                            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                            <span className="text-blue-700 font-medium">💳 Картой</span>
                          </>
                        ) : selectedOrder.payment_method === 'cash' ? (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                            <span className="text-green-700 font-medium">💵 Наличными</span>
                          </>
                        ) : (
                          <span className="text-gray-700">
                            {selectedOrder.payment_method_display || selectedOrder.payment_method || 'Не указан'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Сумма заказа */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">Сумма</h4>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      {formatCurrency(selectedOrder.total_price)}
                    </div>
                    <div className="text-sm text-green-700">
                      {selectedOrder.is_paid ? '✅ Оплачено' : '⏳ Ожидает оплаты'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Адрес доставки */}
              {selectedOrder.delivery_address && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-amber-900">Адрес доставки</h4>
                  </div>
                  <p className="text-amber-800 ml-11">{selectedOrder.delivery_address}</p>
                </div>
              )}

              {/* Комментарий */}
              {selectedOrder.comment && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-blue-900">Комментарий к заказу</h4>
                  </div>
                  <p className="text-blue-800 ml-11 italic">"{selectedOrder.comment}"</p>
                </div>
              )}

              {/* Состав заказа */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <h4 className="font-semibold text-gray-900">Состав заказа</h4>
                      <span className="ml-auto bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {selectedOrder.items.length} {selectedOrder.items.length === 1 ? 'позиция' : 'позиций'}
                      </span>
                    </div>
                  </div>
                                     <div className="divide-y divide-gray-100">
                     {selectedOrder.items.map((item, index) => (
                       <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                         <div className="flex items-start gap-4">
                           {/* Изображение блюда */}
                           <div className="flex-shrink-0 relative">
                             {(() => {
                               const imageKey = `${selectedOrder.id}-${index}`;
                               const imageState = imageLoadingStates[imageKey];
                               
                               if (item.dish_image && imageState !== 'error') {
                                 return (
                                   <div className="relative">
                                     {/* Индикатор загрузки */}
                                     {imageState === 'loading' && (
                                       <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
                                         <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                                       </div>
                                     )}
                                     
                                     <img
                                       src={item.dish_image}
                                       alt={item.dish_name}
                                       className={`w-16 h-16 rounded-lg object-cover border border-gray-200 shadow-sm transition-opacity duration-200 ${
                                         imageState === 'loaded' ? 'opacity-100' : 'opacity-0'
                                       }`}
                                       loading="lazy"
                                       onLoadStart={() => handleImageStart(imageKey)}
                                       onLoad={() => handleImageLoad(imageKey)}
                                       onError={() => handleImageError(imageKey)}
                                     />
                                   </div>
                                 );
                               }
                               
                               // Показываем заглушку если нет изображения или ошибка загрузки
                               return (
                                 <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-200 shadow-sm">
                                   <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                   </svg>
                                 </div>
                               );
                             })()}
                           </div>

                           {/* Информация о блюде */}
                           <div className="flex-1 min-w-0">
                             <div className="flex items-center mb-1">
                               <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0">
                                 {index + 1}
                               </span>
                               <h5 className="font-medium text-gray-900 truncate">{item.dish_name}</h5>
                             </div>
                             {item.comment && (
                               <div className="ml-9 text-sm text-gray-500 italic">
                                 💬 {item.comment}
                               </div>
                             )}
                           </div>

                           {/* Цена и количество */}
                           <div className="text-right flex-shrink-0">
                             <div className="flex items-center justify-end mb-1">
                               <span className="text-sm text-gray-500 mr-2">×{item.quantity}</span>
                               <span className="font-semibold text-gray-900">{formatCurrency(item.total_price)}</span>
                             </div>
                             <div className="text-xs text-gray-400">
                               {formatCurrency(item.total_price / item.quantity)} за шт.
                             </div>
                           </div>
                         </div>
                       </div>
                     ))}
                  </div>
                  
                  {/* Итого */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Итого:</span>
                      <span className="text-2xl font-bold text-primary-600">{formatCurrency(selectedOrder.total_price)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Футер с действиями */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Заказ создан: {formatDate(selectedOrder.created_at)}
              </div>
              <div className="flex gap-3">
                {selectedOrder.status !== 'completed' && selectedOrder.status !== 'cancelled' && (
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => {
                      updateOrderStatus(selectedOrder.id, e.target.value);
                      setSelectedOrder({...selectedOrder, status: e.target.value});
                    }}
                    disabled={updating}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="new">Новый</option>
                    <option value="confirmed">Подтвержден</option>
                    <option value="preparing">Готовится</option>
                    <option value="ready">Готов</option>
                    <option value="completed">Завершен</option>
                    <option value="cancelled">Отменен</option>
                  </select>
                )}
                <button 
                  onClick={closeOrderModal} 
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement; 